import Head from 'next/head'
import { useRouter } from 'next/router'

import { AppConfig } from '@/shared/api'

interface SeoMetaProps {
  title?: string
  description?: string
  image?: string
  url?: string
  siteName?: string
}

export const SeoMeta: React.FC<SeoMetaProps> = ({ title, description, image, url, siteName }) => {
  return (
    <Head>
      {/* Basic Meta Tags */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      {/* Open Graph Meta Tags */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {image && <meta property="og:image" content={image} />}
      {url && <meta property="og:url" content={url} />}
      {siteName && <meta property="og:site_name" content={siteName} />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}

      {/* Additional meta tags for better SEO */}
      {url && <link key="canonical" rel="canonical" href={url} />}
    </Head>
  )
}

interface HomePageSeoMetaProps {
  appConfig: AppConfig
}

export const HomePageSeoMeta: React.FC<HomePageSeoMetaProps> = ({ appConfig }) => {
  const router = useRouter()

  const siteName = appConfig.config_web.name
  const sloganText = appConfig.config_web.slogan?.text

  // Create a more descriptive description
  const description = sloganText ? `${sloganText} | ${siteName}` : siteName

  // Create a concise title
  const title = siteName

  const heroImage = appConfig.config_web.images.home_hero

  // Construct URL that works for both SSR and client
  const tenantDomains =
    (appConfig.tenant as { tenant_domains?: { host_name: string; is_primary?: boolean | null }[] }).tenant_domains ?? []
  const fallbackHostname = tenantDomains.find((domain) => domain.is_primary)?.host_name ?? tenantDomains[0]?.host_name
  const host = typeof window !== 'undefined' ? window.location.host : fallbackHostname
  const url = `//${host}${router.asPath}`

  return <SeoMeta title={title} description={description} image={heroImage || undefined} url={url} siteName={siteName} />
}
